import { useEffect, useRef, useState } from "react";
import ReactCrop, { centerCrop, Crop, makeAspectCrop, PixelCrop } from "react-image-crop";
import { Button } from "@mui/material";
import { Box } from "@mui/system";
import cloneDeep from "lodash.clonedeep";
import "react-image-crop/dist/ReactCrop.css";

import styles from "./cropImage.module.scss";

function centerAspectCrop(mediaWidth: number, mediaHeight: number, aspect: number) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 50,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

export interface CropImageProps {
  imgSrc: string | ArrayBuffer | null;
  onCancel: () => void;
  onDone: (image: Blob | null) => void;
}

function CropImage(props: CropImageProps): JSX.Element {
  const { imgSrc, onCancel, onDone } = props;
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const imgRef = useRef<HTMLImageElement | null>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const aspect = 1;

  /**
   * Save the reference of the original image when is loaded in ReactCrop
   *
   * @param e
   */
  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    if (aspect) {
      const { width, height } = e.currentTarget;
      setCrop(centerAspectCrop(width, height, aspect));
    }
  }
  /**
   * Handle finish crop
   *
   * @returns
   */
  const handleDone = () => {
    if (previewCanvasRef.current && completedCrop) {
      previewCanvasRef.current.toBlob((blob) => {
        onDone(blob);
      }, "image/jpeg");
    }
  };

  /**
   * Draw the image in a canvas to be able to save it later as Blob
   */
  useEffect(() => {
    if (completedCrop && previewCanvasRef.current && imgRef.current) {
      const image = imgRef.current;
      const canvas = previewCanvasRef.current;
      const clonedCompletedCrop = cloneDeep(completedCrop);

      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      const ctx = canvas.getContext("2d");
      const pixelRatio = window.devicePixelRatio;

      canvas.width = clonedCompletedCrop.width * pixelRatio * scaleX;
      canvas.height = clonedCompletedCrop.height * pixelRatio * scaleY;

      if (ctx) {
        ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        ctx.imageSmoothingQuality = "high";

        ctx.drawImage(
          image,
          clonedCompletedCrop.x * scaleX,
          clonedCompletedCrop.y * scaleY,
          clonedCompletedCrop.width * scaleX,
          clonedCompletedCrop.height * scaleY,
          0,
          0,
          clonedCompletedCrop.width * scaleX,
          clonedCompletedCrop.height * scaleY
        );
      }
    }
  }, [completedCrop]);

  return (
    <>
      <div className={styles.reactCropContainer}>
        {typeof imgSrc === "string" ? (
          <ReactCrop
            aspect={1}
            circularCrop
            className={styles.reactCrop}
            crop={crop}
            keepSelection
            minHeight={96}
            minWidth={96}
            onChange={(c) => setCrop(c)}
            onComplete={(c) => setCompletedCrop(c)}
          >
            <img alt='to crop' onLoad={onImageLoad} ref={imgRef} src={imgSrc} style={{ maxHeight: "75vh" }} />
          </ReactCrop>
        ) : null}
      </div>
      <canvas
        hidden
        ref={previewCanvasRef}
        style={{
          width: Math.round(completedCrop?.width ?? 0),
          height: Math.round(completedCrop?.height ?? 0),
        }}
      />
      <Box className={styles.actionsButtons}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={handleDone}>Done</Button>
      </Box>
    </>
  );
}

export default CropImage;
